<template>
  <div>
    <div v-if="showTabs">
      <ul class="nav nav-tabs">
        <li v-for="(tab, index) in tabs" :key="index" class="nav-item" :class="{ dropdown: tab.children }">
          <!-- Wyświetla dropdown -->
          <check-permission v-if="tab.children" join-type="OR" :permissions="getChildrenPermissions(tab)">
            <dropdown :entity-type="entityType" :entity-id="entityId" :tab="tab" />
          </check-permission>

          <!-- Wyświetla zakładke -->
          <check-permission
            v-else
            :entity-id="tab.entityId || entityId"
            :entity-type="tab.entityType || entityType"
            :permissions="tab.permissions"
            :join-type="tab.joinType"
          >
            <tab-link class="nav-link" :tab="tab" />

            <template v-if="tab.noPermission" #noPermission>
              <!-- Na razie tylko opcja dla pojedynczej zakładki -->
              <check-permission
                :entity-id="tab.noPermission.entityId || entityId"
                :entity-type="tab.noPermission.entityType || entityType"
                :permissions="tab.noPermission.permissions"
                :join-type="tab.noPermission.joinType"
              >
                <tab-link class="nav-link" :tab="tab.noPermission" />
              </check-permission>
            </template>
          </check-permission>
        </li>
      </ul>

      <div class="tab-content">
        <slot>
          <tabs
            v-if="activeTab.children && activeTab.children.length"
            :tabs="activeTab.children"
            :entity-id="entityId"
            :entity-type="entityType"
          />

          <router-view v-else />
        </slot>
      </div>
    </div>

    <div v-else>
      <router-view />
    </div>
  </div>
</template>

<script>
import { tabsMixin } from '@/mixins/tabs';
import { flat } from '@/utils/flat';

import Dropdown from './Dropdown.vue';
import TabLink from './TabLink.vue';

// @vuese
// Zakładki
// @group Components
export default {
  name: 'Tabs',
  components: { Dropdown, TabLink },
  mixins: [tabsMixin],
  props: {
    // Lista zakładek
    // format: `{title: string, route: object, permissions?: string[]}`
    // Jeżeli zależy nam, żeby routing zakładki nie miał danego parametru, to trzeba przekazać go jako undefined
    tabs: {
      type: Array,
      required: true
    },
    // Typ encji
    entityType: {
      type: String,
      required: false
    },
    // Identyfikator encji
    entityId: {
      type: String,
      required: false
    }
  },
  computed: {
    // Sprawdza, czy zakładki powinny być widoczne
    showTabs() {
      return !!this.activeTab;
    },
    activeTab() {
      return this.tabs.find(
        tab =>
          this.isActive(tab) ||
          (tab.children && tab.children.some(childTab => this.isActive(childTab))) ||
          (tab.noPermission && this.isActive(tab.noPermission))
      );
    }
  },
  methods: {
    // @vuese
    // Zwraca ciąg uprawnienia z encją
    permissionWithEntity(permission, child, tab) {
      if (child.entityType && child.entityId) {
        return `${permission}:${child.entityType}:${child.entityId}`;
      }

      if (tab.entityType && tab.entityId) {
        return `${permission}:${tab.entityType}:${tab.entityId}`;
      }

      if (this.entityType && this.entityId) {
        return `${permission}:${this.entityType}:${this.entityId}`;
      }

      return permission;
    },
    // @vuese
    // Zwraca uprawnienia dzieci
    getChildrenPermissions(tab) {
      if (!tab.children || !tab.children.length) {
        return;
      }
      const childrenPermissions = flat(
        tab.children
          .filter(child => !!child.permissions && child.permissions.length)
          .map(child => {
            if (!child.entityId || !Array.isArray(child.entityId)) {
              return child;
            }
            if (!child.entityId.length) {
              return { ...child, entityId: undefined };
            }
            return child.entityId.map(entityId => ({ ...child, entityId }));
          })
          .reduce((allChildren, currentChildren) => allChildren.concat(currentChildren), [])
          .map(child => child.permissions.map(permission => this.permissionWithEntity(permission, child, tab)))
      );
      return [...new Set(childrenPermissions)];
    }
  }
};
</script>
